import { Flowplayer as FlowplayerType } from '@flowplayer/player';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, StyleSheet, View } from 'react-native';
import { isOffAirTime } from 'utils/date';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';

import VideoPlayer from '../Video/index';
import { usePlayer } from '../context';
import CTVPlayerWithAds from './ads';
import { CustomControls } from './controls';

import { Icons } from 'components/Icons';
import { WebDiv } from 'components/WebDiv';

const BACK_EVENT = 'back-remote-control';
const bandThumb = require('../../../assets/band_thumb.png') as string;

const CTVPlayer = React.forwardRef<
  CustomPlayerRef,
  CustomPlayerProps & { disableControls?: boolean }
>((props) => {
  const {
    ref: playerRef,
    isPlaying,
    isVisible,
    setIsVisible,
    setShowingAdsCTV,
    isShowingAdsCTV,
    adTagUrl,
    setFullScreen,
    isLive,
    pause,
  } = usePlayer();
  const { TransmissionScreen } = useGlobalConfig();
  const { selectedProfile } = useSelectedProfile();
  const isAdultProfile = selectedProfile?.ageRating === '18';
  const isOffAir = TransmissionScreen?.livestream_offline_mode === true && isLive && isOffAirTime();

  const [isInitializing, setIsInitializing] = React.useState(true);

  const containerClass = React.useMemo(() => {
    if (isVisible) return 'navigation-container fullscreen';
    return 'navigation-container navigation-ignore navigation-detail-page';
  }, [isVisible]);

  const playerApi = React.useMemo(
    () => playerRef?.current?.getNativePlayerRef() as FlowplayerType.Player,
    [playerRef.current]
  );

  useEffect(() => {
    if (!playerApi) return;
    setIsInitializing(false);
  }, [playerApi]);

  useEffect(() => {
    if (!isVisible) playerApi?.pause();
  }, [isVisible, playerApi]);

  useEffect(() => {
    return () => setShowingAdsCTV(true);
  }, []);

  const handleBack = useCallback(() => {
    setFullScreen(false);
    setIsVisible(false);
    pause();
  }, [setIsVisible, pause, setFullScreen]);

  return createPortal(
    <WebDiv className={containerClass}>
      <View style={styles.container}>
        {isOffAir && isVisible ? (
          <OffAirPlayer
            exit={() => {
              pause();
              setIsVisible(false);
            }}
          />
        ) : !isOffAir && isShowingAdsCTV && adTagUrl && isAdultProfile ? (
          <CTVPlayerWithAds {...props} />
        ) : !isOffAir ? (
          <>
            <VideoPlayer {...props} ref={playerRef} />
            {!isInitializing && <CustomControls api={playerApi} exit={handleBack} />}
          </>
        ) : null}
      </View>
    </WebDiv>,
    document.querySelector('#root')!
  );
});

function OffAirPlayer({ exit }: { exit: () => void }) {
  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      const isBack = [8, 27, 461, 10009].includes(e.keyCode);

      if (!isBack) return;

      e.preventDefault();
      e.stopPropagation();
      exit();
    },
    [exit]
  );

  const handleBackEvent = React.useCallback(() => exit(), [exit]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, false);
    window.addEventListener(BACK_EVENT, handleBackEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener(BACK_EVENT, handleBackEvent);
    };
  }, [handleBackEvent, handleKeyDown]);

  return (
    <WebDiv className="navigation-container navigation-priority" style={styles.offAirContainer}>
      <img src={bandThumb} alt="" style={offAirImageStyle} />

      <View style={styles.offAirControls}>
        <Pressable accessibilityRole="button" onPress={exit} style={styles.backButton}>
          <Icons.TurnBack
            width={calcSizeForDevice(60).toString()}
            height={calcSizeForDevice(60).toString()}
          />
        </Pressable>
      </View>
    </WebDiv>
  );
}

const offAirImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#000',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  offAirContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
    position: 'relative',
  },
  offAirControls: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: calcSizeForTv(16),
    zIndex: 10,
  },
  backButton: {
    alignSelf: 'flex-start',
  },
});

export default CTVPlayer;
